/**
 * styles.ts
 *
 * Configuration-driven styling system for PlatinumTable component.
 * Implements strict no-double-border rendering using neighbor information.
 */

import React from 'react'
import { GridCell } from './layout'
import type { PlatinumProperties } from '../../types/carbon/models/PlatinumProperties'
import type { XDecimals } from '../../types/carbon/models/XDecimals'
import type { PVecType } from '../../types/carbon/models/PVecType'
import { TableDesign } from '../../types/gotypes'
import { evaluateConditionalFormat } from '../../utils/tableConditionalFormat'

// ============================================================================
// CONFIGURATION - Single source of truth for all style values
// ============================================================================

/**
 * Color Palette
 * All colors used in the table are defined here for easy customization
 */
export const COLORS = {
  // Data cell band colors
  FREQUENCY: '#000000',
  COLUMN_PERCENT: '#0066CC',
  ROW_PERCENT: '#CC0000',
  SIGNIFICANCE: '#6c757d',

  // Border colors — light, Stripe-style dividers
  BORDER_LIGHT: 'rgba(0, 0, 0, 0.04)',   // Within same region (subtle)
  BORDER_MEDIUM: 'rgba(0, 0, 0, 0.06)',  // Between regions (matches theme divider)
  BORDER_DARK: 'rgba(0, 0, 0, 0.10)',    // Group/vector boundaries (prominent)

  // Background colors
  BG_WHITE: '#ffffff',
  BG_ZEBRA: '#f8f9fa',          // Odd row striping (near-white)
  BG_EMPTY: '#f8f9fa',
  BG_GROUP: '#f8f9fa',          // Group headers (near-white)
  BG_LABEL: '#f1f3f5',
} as const

/**
 * Dimensions
 */
export const DIMENSIONS = {
  SIDE_LABEL_WIDTH: 160,   // Width for side headers (groups, labels)
  DATA_CELL_WIDTH: 80,      // Width for data columns
  ROW_HEIGHT: 32,           // Fixed height for all rows
} as const

/**
 * Label Typography
 * Fixed typography for calculating line-clamp
 */
export const LABEL_TYPOGRAPHY = {
  FONT_SIZE: 12,            // Font size in pixels
  LINE_HEIGHT: 16,          // Line height in pixels (allows ~4px spacing)
  CELL_PADDING_VERTICAL: 12, // Total vertical padding (6px top + 6px bottom)
  MAX_LINES: 3,             // Hard cap to prevent unbounded growth with high vertical_coefficient
} as const

/**
 * Scaling coefficients
 */
export const COEFFICIENTS = {
  HORIZONTAL_MIN: 0.4,
  HORIZONTAL_MAX: 3.0,
  HORIZONTAL_DEFAULT: 1.0,
  VERTICAL_MIN: 0.5,
  VERTICAL_MAX: 3.0,
  VERTICAL_DEFAULT: 1.0,
} as const

// Export individual constants for backward compatibility with other components
export const SIDE_LABEL_WIDTH = DIMENSIONS.SIDE_LABEL_WIDTH
export const DATA_CELL_WIDTH = DIMENSIONS.DATA_CELL_WIDTH
export const ROW_HEIGHT = DIMENSIONS.ROW_HEIGHT

export const MIN_HORIZONTAL_COEFFICIENT = COEFFICIENTS.HORIZONTAL_MIN
export const MAX_HORIZONTAL_COEFFICIENT = COEFFICIENTS.HORIZONTAL_MAX
export const DEFAULT_HORIZONTAL_COEFFICIENT = COEFFICIENTS.HORIZONTAL_DEFAULT
export const MIN_VERTICAL_COEFFICIENT = COEFFICIENTS.VERTICAL_MIN
export const MAX_VERTICAL_COEFFICIENT = COEFFICIENTS.VERTICAL_MAX
export const DEFAULT_VERTICAL_COEFFICIENT = COEFFICIENTS.VERTICAL_DEFAULT
export const COEFFICIENT_STEP = 0.2

// Export basic styles object for backward compatibility
export const styles: { [key: string]: React.CSSProperties } = {
  container: {
    overflow: 'auto',
    width: '100%',
    maxHeight: 'calc(100vh - 200px)',
  },
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: '12px',
    tableLayout: 'fixed',
  },
  cell: {
    textAlign: 'right',
    verticalAlign: 'middle',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    padding: '24px',
    textAlign: 'center',
    color: '#6c757d',
    fontStyle: 'italic',
  },
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the actual cell at a position (resolves occupied cells to source)
 * This is a local copy of the function from layout.ts to avoid circular dependencies
 */
function getCellAtPosition(
  grid: GridCell[][],
  rowIdx: number,
  colIdx: number
): GridCell | null {
  if (rowIdx < 0 || rowIdx >= grid.length || colIdx < 0 || colIdx >= grid[0].length) {
    return null
  }

  const cell = grid[rowIdx][colIdx]
  if (!cell) return null

  // If it's an occupied cell, search for the source cell
  if (cell.type === 'occupied') {
    // Search upward and leftward to find the spanning cell
    for (let r = rowIdx; r >= 0; r--) {
      for (let c = colIdx; c >= 0; c--) {
        const sourceCell = grid[r][c]
        if (sourceCell && sourceCell.type !== 'occupied') {
          // Check if this cell spans into our target position
          if (r + sourceCell.rowspan > rowIdx && c + sourceCell.colspan > colIdx) {
            return sourceCell
          }
        }
      }
    }
    return null
  }

  return cell
}

/**
 * Check if a cell is blank (either empty type or empty content)
 * Used for visual merging of blank cells
 */
function isBlankCell(cell: GridCell | null): boolean {
  if (!cell) return false
  return cell.type === 'empty' || cell.content.trim() === ''
}

/**
 * Check if two cells are splits from the same original cell
 * Used to hide internal borders between split cells
 */
function isSplitFromSame(cell: GridCell, neighbor: GridCell | null): boolean {
  if (!cell.splitInfo || !neighbor?.splitInfo) {
    return false
  }
  return cell.splitInfo.originalCellId === neighbor.splitInfo.originalCellId
}

// ============================================================================
// LINE-CLAMP CALCULATION
// ============================================================================

/**
 * Calculate the maximum number of lines that can fit in a cell
 * based on its rowspan and vertical coefficient.
 *
 * This enables dynamic line-clamping for side header cells (groups and labels)
 * to prevent text overflow from increasing cell height.
 *
 * @param cell - The grid cell
 * @param verticalCoefficient - Vertical scaling coefficient
 * @returns The maximum number of lines that fit in the cell's height
 */
export function calculateMaxLines(
  cell: GridCell,
  verticalCoefficient: number = COEFFICIENTS.VERTICAL_DEFAULT
): number {
  // Calculate total cell height based on rowspan
  const cellHeight = getCellHeight(cell, verticalCoefficient)
  const totalHeight = cell.rowspan * cellHeight

  // Subtract padding to get available height for text
  const availableHeight = totalHeight - LABEL_TYPOGRAPHY.CELL_PADDING_VERTICAL

  // Calculate how many lines fit
  // Using Math.floor to ensure we don't partially show a line
  const maxLines = Math.floor(availableHeight / LABEL_TYPOGRAPHY.LINE_HEIGHT)

  return Math.min(Math.max(maxLines, 1), LABEL_TYPOGRAPHY.MAX_LINES)
}

/**
 * Get CSS properties for line-clamp wrapper element
 *
 * These styles are applied to a wrapper element (span/div) INSIDE the <td>,
 * not to the <td> itself, to avoid breaking table layout.
 *
 * @param maxLines - Maximum number of lines to display
 * @returns CSS properties for the wrapper element
 */
export function getLineClampWrapperStyle(maxLines: number): React.CSSProperties {
  return {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: maxLines,
    overflow: 'hidden',
    fontSize: `${LABEL_TYPOGRAPHY.FONT_SIZE}px`,
    lineHeight: `${LABEL_TYPOGRAPHY.LINE_HEIGHT}px`,
    // Ensure text wrapping is enabled
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  }
}

// ============================================================================
// DIMENSION FUNCTIONS
// ============================================================================

/**
 * Calculate width for a cell
 */
export function getCellWidth(
  cell: GridCell,
  horizontalCoefficient: number = COEFFICIENTS.HORIZONTAL_DEFAULT,
  compactSideLabels: boolean = false,
): number {
  // Side header cells use scaled width (or compact width)
  if (cell.position?.isSideHeader) {
    if (compactSideLabels) return 32
    return DIMENSIONS.SIDE_LABEL_WIDTH * horizontalCoefficient
  }

  // All other cells (data columns, top headers) use data width
  return DIMENSIONS.DATA_CELL_WIDTH
}

/**
 * Calculate height for a cell
 */
export function getCellHeight(
  cell: GridCell,
  verticalCoefficient: number = COEFFICIENTS.VERTICAL_DEFAULT
): number {
  // Top header and corner cells use scaled height
  if (cell.position?.isTopHeader || cell.position?.isCorner) {
    return DIMENSIONS.ROW_HEIGHT * verticalCoefficient
  }

  // All other cells use base height
  return DIMENSIONS.ROW_HEIGHT
}

// ============================================================================
// COLOR AND TEXT STYLING
// ============================================================================

/**
 * Get background color based on cell type
 *
 * `design` (#969 Part C) is the RAW TableDesign, not the resolved one — same
 * convention as applyChartDesign.ts: only explicitly-overridden properties
 * are patched on top of the computation below, so a table whose design was
 * never touched renders pixel-identical to before this feature existed (the
 * #968 Part C lesson, commit 466740d3a). Resolution to design-system
 * defaults is for the editor's controls (utils/tableDesign.ts); this render
 * path needs to know which properties are actually set.
 *
 * Priority for data cells: conditional-formatting rule (if any matches)
 * wins over the zebra-stripe base. Header background overrides only apply
 * to non-empty group/label cells — empty ones stay white so the existing
 * blank-cell visual merging (see isBlankCell) is unaffected.
 */
export function getBackgroundColor(
  cell: GridCell,
  isOddRow: boolean,
  design?: TableDesign
): string {
  const hasEmptyContent = cell.content.trim() === ''

  const base = (() => {
    switch (cell.type) {
      case 'title':
        return COLORS.BG_EMPTY
      case 'group':
        return hasEmptyContent ? COLORS.BG_WHITE : COLORS.BG_GROUP
      case 'label':
        return hasEmptyContent ? COLORS.BG_WHITE : COLORS.BG_LABEL
      case 'letter':
        return COLORS.BG_WHITE
      case 'data':
        return isOddRow ? COLORS.BG_ZEBRA : COLORS.BG_WHITE
      case 'empty':
        return COLORS.BG_WHITE
      default:
        return isOddRow ? COLORS.BG_ZEBRA : COLORS.BG_WHITE
    }
  })()

  if (!design) return base

  if (!hasEmptyContent && cell.type === 'group' && design.header?.group?.background_color) {
    return design.header.group.background_color
  }
  if (!hasEmptyContent && cell.type === 'label' && design.header?.label?.background_color) {
    return design.header.label.background_color
  }

  if (cell.type === 'data') {
    const formatted = evaluateConditionalFormat(cell, design.conditional_formatting)
    if (formatted?.backgroundColor) return formatted.backgroundColor
  }

  return base
}

/**
 * Get text color based on cell type and band
 *
 * See getBackgroundColor's doc comment for the raw-design / patch-only-what
 * -changed convention `design` follows here.
 */
export function getTextColor(cell: GridCell, design?: TableDesign): string {
  const base = (() => {
    // Data cell colors based on band
    if (cell.type === 'data' && cell.bandStyle?.band) {
      switch (cell.bandStyle.band) {
        case 'freq':
          return COLORS.FREQUENCY
        case 'colpc':
          return COLORS.COLUMN_PERCENT
        case 'rowpc':
          return COLORS.ROW_PERCENT
        case 'sig':
        case 'stat1':
        case 'stat2':
          return COLORS.SIGNIFICANCE
      }
    }

    // Letter cells
    if (cell.type === 'letter') {
      return '#666'
    }

    // Default: black
    return '#000'
  })()

  if (!design) return base

  if (cell.type === 'group' && design.header?.group?.text_color) {
    return design.header.group.text_color
  }
  if (cell.type === 'label' && design.header?.label?.text_color) {
    return design.header.label.text_color
  }

  // Significance treatment (#969 Part C) — unifies letter cells and
  // sig/stat data bands under one configurable colour when overridden.
  const isSignificanceCell =
    cell.type === 'letter' ||
    (cell.type === 'data' && (cell.bandStyle?.band === 'sig' || cell.bandStyle?.band === 'stat1' || cell.bandStyle?.band === 'stat2'))
  if (isSignificanceCell && design.significance?.color) {
    return design.significance.color
  }

  if (cell.type === 'data') {
    const formatted = evaluateConditionalFormat(cell, design.conditional_formatting)
    if (formatted?.textColor) return formatted.textColor
  }

  return base
}

/**
 * Get font styles based on cell type
 *
 * See getBackgroundColor's doc comment for the raw-design / patch-only-what
 * -changed convention `design` (#969 Part C) follows here. Cell-level
 * overrides (font size, alignment) apply to data cells specifically — the
 * "Cell formatting" bullet in #969 Part C, distinct from "Header styling"
 * (font weight per header level, applied to group/label cells below).
 * Significance emphasis applies to the same cell set getTextColor's
 * significance-colour override does (letter cells, sig/stat data bands).
 */
export function getFontStyle(cell: GridCell, design?: TableDesign): Partial<React.CSSProperties> {
  const style: Partial<React.CSSProperties> = {
    textAlign: 'center',
  }

  // Title cells
  if (cell.type === 'title') {
    style.fontWeight = 600
  }

  // Significance cells (smaller font)
  if (cell.type === 'data' && (cell.bandStyle?.band === 'sig' || cell.bandStyle?.band === 'stat1' || cell.bandStyle?.band === 'stat2')) {
    style.fontSize = '11px'
  }

  // Letter cells
  if (cell.type === 'letter') {
    style.fontWeight = 500
    style.fontSize = '11px'
  }

  // Text wrapping for groups and labels
  if (cell.type === 'group' || cell.type === 'label') {
    // For side header groups/labels, typography will be handled by line-clamp wrapper
    // so we only set minimal styles here
    const isSideHeader = cell.position?.isSideHeader ?? false

    if (isSideHeader || (cell.position?.isTopHeader ?? false)) {
      // Side and top headers: let wrapper handle typography and wrapping
      style.fontSize = `${LABEL_TYPOGRAPHY.FONT_SIZE}px`
      style.lineHeight = `${LABEL_TYPOGRAPHY.LINE_HEIGHT}px`
    }
  } else {
    style.whiteSpace = 'nowrap'
    style.overflow = 'hidden'
    style.textOverflow = 'ellipsis'
  }

  if (design) {
    if (cell.type === 'group' && design.header?.group?.font_weight != null) {
      style.fontWeight = design.header.group.font_weight
    }
    if (cell.type === 'label' && design.header?.label?.font_weight != null) {
      style.fontWeight = design.header.label.font_weight
    }

    if (cell.type === 'data') {
      if (design.cell?.font_size != null) {
        style.fontSize = `${design.cell.font_size}px`
      }
      if (design.cell?.alignment) {
        style.textAlign = design.cell.alignment
      }
    }

    const isSignificanceCell =
      cell.type === 'letter' ||
      (cell.type === 'data' && (cell.bandStyle?.band === 'sig' || cell.bandStyle?.band === 'stat1' || cell.bandStyle?.band === 'stat2'))
    if (isSignificanceCell && design.significance?.emphasis) {
      if (design.significance.emphasis === 'bold') {
        style.fontWeight = 700
      } else if (design.significance.emphasis === 'italic') {
        style.fontStyle = 'italic'
      }
      // 'none' leaves the styling above untouched.
    }
  }

  return style
}

// ============================================================================
// BORDER LOGIC (STRICT NO-DOUBLE-BORDER)
// ============================================================================

/**
 * Border configuration for a cell
 */
interface BorderStyles {
  top: string;
  right: string;
  bottom: string;
  left: string;
}

/**
 * Get borders for a cell using strict no-double-border logic
 *
 * Core principle: Each cell is responsible for TOP and LEFT borders only,
 * but must defer to neighbors who have already rendered on shared edges.
 *
 * Blank cell merging: No borders between adjacent blank cells (both type='empty' or content='')
 *
 * `design.cell` (#969 Part C — "Cell formatting: borders, gridlines") scopes
 * to DATA cell borders only, leaving the structural header/group/label
 * border logic below untouched — that logic separates hierarchy levels and
 * isn't a themeable "gridline". See getBackgroundColor's doc comment for the
 * raw-design / patch-only-what-changed convention. `gridlines_visible ===
 * false` suppresses all of a data cell's borders; otherwise `border_color`/
 * `border_width` replace the dark/light constants only when explicitly set,
 * so an untouched table keeps the exact original border strings.
 */
export function getBorders(
  cell: GridCell,
  grid: GridCell[][],
  design?: TableDesign
): BorderStyles {

  const gridlinesVisible = design?.cell?.gridlines_visible !== false
  const hasBorderOverride = design?.cell?.border_color != null || design?.cell?.border_width != null
  const overrideWidth = design?.cell?.border_width ?? 1
  const overrideColor = design?.cell?.border_color

  const darkBorder = `1px solid ${COLORS.BORDER_DARK}`
  const lightBorder = `1px solid ${COLORS.BORDER_MEDIUM}`
  const dataDarkBorder = hasBorderOverride ? `${overrideWidth}px solid ${overrideColor ?? COLORS.BORDER_DARK}` : darkBorder
  const dataLightBorder = hasBorderOverride ? `${overrideWidth}px solid ${overrideColor ?? COLORS.BORDER_MEDIUM}` : lightBorder

  const borders: BorderStyles = {
    top: '0',
    right: '0',
    bottom: '0',
    left: '0'
  }

  if(!cell.position) return borders

  const aboveCell = getCellAtPosition(grid, cell.position.rowIdx - 1, cell.position.colIdx);
  const belowCell = getCellAtPosition(grid, cell.position.rowIdx + 1, cell.position.colIdx);
  const leftCell = getCellAtPosition(grid, cell.position.rowIdx, cell.position.colIdx - 1);
  const rightCell = getCellAtPosition(grid, cell.position.rowIdx, cell.position.colIdx + 1);

  if(cell.type === 'empty') {
    // Empty cells add bottom/right borders toward non-blank neighbors.
    // Non-blank neighbors handle their own bottom/right borders toward us,
    // so we only need to cover the edges they don't own (our bottom and right).
    if (!isBlankCell(belowCell) && belowCell !== null) {
      borders.bottom = darkBorder
    }
    if (!isBlankCell(rightCell) && rightCell !== null) {
      borders.right = darkBorder
    }
    // At grid edges, no neighbor exists to provide a border, so we add our own.
    // Skip left border — the sidebar/container provides the left edge.
    // Skip top border at row 0 — the table wrapper already provides borderTop.
    if (aboveCell === null && cell.position.rowIdx !== 0) {
      borders.top = darkBorder
    }
    return borders
  }

  if (cell.type === 'data') {
    if (gridlinesVisible) {
      // Right border: dark if at edge or next to empty, otherwise light
      if (rightCell === null || isBlankCell(rightCell)) {
        borders.right = dataDarkBorder
      } else {
        borders.right = dataLightBorder
      }

      // Bottom border: dark if at edge, next to empty, or last in vector
      if (belowCell === null || isBlankCell(belowCell) || cell.position?.isLastInVector) {
        borders.bottom = dataDarkBorder
      }

      // Top border: light separator above sig band rows to visually separate from value rows
      if (cell.bandStyle?.band === 'sig') {
        borders.top = dataLightBorder
      }
    }

    // No left border — the container provides the left edge
  } else if(!isBlankCell(cell)) {
    borders.right = darkBorder
    borders.bottom = darkBorder

    // Top border only at grid edges — skip row 0 (table wrapper provides it)
    if(aboveCell === null && cell.position.rowIdx !== 0) {
      borders.top = darkBorder
    }
    // No left border — the container provides the left edge

    // Hide internal borders between split cells from the same original
    // But preserve borders on the edges of the split block
    if(isSplitFromSame(cell, rightCell) && !cell.splitInfo?.isLastCol) {
      borders.right = '0'
    }

    if(isSplitFromSame(cell, belowCell) && !cell.splitInfo?.isLastRow) {
      borders.bottom = '0'
    }

    if(isSplitFromSame(cell, leftCell) && !cell.splitInfo?.isFirstCol) {
      borders.left = '0'
    }

    if(isSplitFromSame(cell, aboveCell) && !cell.splitInfo?.isFirstRow) {
      borders.top = '0'
    }
  }

  return borders
}

// ============================================================================
// MAIN STYLE COMPOSITION
// ============================================================================

/**
 * Options for getCellStyle
 */
export interface CellStyleOptions {
  grid: GridCell[][];
  horizontalCoefficient?: number;
  verticalCoefficient?: number;
  isOddRow?: boolean;
  leftOffset?: number;
  topOffset?: number;
  disableSticky?: boolean;
  compactSideLabels?: boolean;
  /**
   * Explicit pixel width for this cell, overriding the computed one (#969
   * Part C — per-column widths saved on the table widget). Already summed
   * across a spanning cell's columns by the caller.
   */
  width?: number;
  /** Table design overrides (#969 Part C) — raw (not resolved), patched onto the defaults below only where explicitly set. */
  design?: TableDesign;
}

/**
 * Get complete inline style for a cell
 * Composes all style functions into final React.CSSProperties object
 */
export function getCellStyle(
  cell: GridCell,
  options: CellStyleOptions
): React.CSSProperties {
  const {
    grid,
    horizontalCoefficient = COEFFICIENTS.HORIZONTAL_DEFAULT,
    verticalCoefficient = COEFFICIENTS.VERTICAL_DEFAULT,
    isOddRow = false,
    leftOffset,
    topOffset,
    disableSticky = false,
    compactSideLabels = false,
    width: explicitWidth,
    design,
  } = options

  // Calculate dimensions
  const width = explicitWidth ?? getCellWidth(cell, horizontalCoefficient, compactSideLabels)
  const height = getCellHeight(cell, verticalCoefficient)

  // Get borders
  const borders = getBorders(cell, grid, design)

  // Base style
  const baseStyle: React.CSSProperties = {
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
    height: `${height}px`,
    backgroundColor: getBackgroundColor(cell, isOddRow, design),
    color: getTextColor(cell, design),
    ...getFontStyle(cell, design),
    borderTop: borders.top,
    borderRight: borders.right,
    borderBottom: borders.bottom,
    borderLeft: borders.left,
    padding: '6px 8px',
    verticalAlign: 'middle',
    cursor: cell.type === 'data' ? 'pointer' : 'default',
  }

  // Add sticky positioning for header cells (unless disabled)
  if (!disableSticky) {
    if (cell.position?.isTopHeader && cell.position?.isSideHeader) {
      // Corner cells: sticky both directions
      baseStyle.position = 'sticky'
      baseStyle.left = leftOffset !== undefined ? `${leftOffset}px` : 0
      baseStyle.top = topOffset !== undefined ? `${topOffset}px` : 0
      baseStyle.zIndex = 4
    } else if (cell.position?.isTopHeader) {
      // Top header cells: sticky top
      baseStyle.position = 'sticky'
      baseStyle.top = topOffset !== undefined ? `${topOffset}px` : 0
      baseStyle.zIndex = 3
    } else if (cell.position?.isSideHeader) {
      // Side header cells: sticky left
      baseStyle.position = 'sticky'
      baseStyle.left = leftOffset !== undefined ? `${leftOffset}px` : 0
      baseStyle.zIndex = 2
    } else {
      // Data cells: regular positioning
      baseStyle.zIndex = 1
    }
  } else {
    // When sticky is disabled, just set zIndex for proper layering
    baseStyle.zIndex = 1
  }

  return baseStyle
}


/**
 * Get decimals for a cell based on side vector type and band.
 *
 * Logic (from Roland's clarification):
 * - Percent bands (colpc, rowpc) always use decimals.percents
 * - Freq band varies by vec type:
 *   - Function/Arith → decimals.expressions
 *   - Stat → decimals.statistics
 *   - Base/Code/Net/other → decimals.frequencies
 */
function getDecimalsForVecType(
  vecType: PVecType | string | undefined,
  band: string,
  decimals: XDecimals | undefined
): number {
  // Percent bands ALWAYS use percents decimals (regardless of vec type)
  if (band === 'colpc' || band === 'rowpc') {
    return decimals?.percents ?? 1
  }

  // Freq band varies by vec type
  switch (vecType) {
    case 'Function':
    case 'Arith':
      return decimals?.expressions ?? 3
    case 'Stat':
      return decimals?.statistics ?? 2
    default:
      return decimals?.frequencies ?? 0
  }
}

/**
 * Format a data cell value for display
 *
 * Optional helper function for renderers to format sourceData based on band
 * From C#: XDisplayTab.cs CellContents() (lines 684-766)
 *
 * @param sourceData - Cell data containing freq, colpc, rowpc, sig
 * @param band - Band type ('freq', 'colpc', 'rowpc', 'stat1', 'stat2')
 * @param props - Platinum properties for formatting
 * @param sideVecType - Side vector type for decimal calculation
 * @param sideAllowPC - Whether percentages are allowed for this row (from sideVec.allowPC)
 * @param topAllowPC - Whether percentages are allowed for this column (from topVec.allowPC)
 */
export function formatCellValue(
  sourceData: GridCell['sourceData'],
  band: string,
  props: PlatinumProperties,
  sideVecType?: PVecType | string,
  sideAllowPC?: boolean,
  topAllowPC?: boolean
): string {
  if (!sourceData) return ''

  // Helper to append significance letters if enabled
  const maybeAppendSig = (formatted: string): string => {
    if (props.significance?.visible && props.significance?.appendLetters && sourceData.sig) {
      return `${formatted} ${sourceData.sig}`
    }
    return formatted
  }

  switch (band) {
    case 'freq': {
      const decimals = getDecimalsForVecType(sideVecType, 'freq', props.decimals)
      const value = sourceData.freq ?? 0

      // Handle missing/zero/blank
      if (value === 0) {
        if (props.cells?.missingAsZero) return maybeAppendSig('0')
        if (props.cells?.zeroAsBlank) {
          return props.cells?.blankAsChar ? (props.cells?.blankChar ?? '') : ''
        }
      }

      return maybeAppendSig(value.toFixed(decimals))
    }

    case 'colpc':
    case 'rowpc': {
      // Show percentages only if BOTH vectors allow it
      // undefined is treated as true (default for backward compatibility)
      const canShowPercent = sideAllowPC !== false && topAllowPC !== false

      // If either vector disallows percentages, show freq data formatted as freq
      if (!canShowPercent) {
        const decimals = getDecimalsForVecType(sideVecType, 'freq', props.decimals)
        const value = sourceData.freq ?? 0

        // Handle missing/zero/blank
        if (value === 0) {
          if (props.cells?.missingAsZero) return maybeAppendSig('0')
          if (props.cells?.zeroAsBlank) {
            return props.cells?.blankAsChar ? (props.cells?.blankChar ?? '') : ''
          }
        }

        return maybeAppendSig(value.toFixed(decimals))
      }

      let value = band === 'colpc' ? (sourceData.colpc ?? 0) : (sourceData.rowpc ?? 0)

      // Handle missing/zero/blank
      if (value === 0) {
        if (props.cells?.missingAsZero) value = 0
        else if (props.cells?.zeroAsBlank) {
          return props.cells?.blankAsChar ? (props.cells?.blankChar ?? '') : ''
        }
      }

      // Convert to percentage if not already
      if (!props.cells?.percentsAsProportions) {
        value *= 100
      }

      const decimals = getDecimalsForVecType(sideVecType, band, props.decimals)
      let formatted = value.toFixed(decimals)

      // Add percent sign if enabled
      if (props.cells?.percentSign) {
        formatted += '%'
      }

      return maybeAppendSig(formatted)
    }

    case 'stat1':
    case 'stat2':
      return sourceData.sig ?? ''

    default:
      return ''
  }
}